import React, { useEffect, useState, useMemo } from 'react';
import { feeAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiX, FiTrash2, FiSearch, FiUsers } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';

const FeeGroup = () => {
  const { user } = useAuth();
  const [feeGroups, setFeeGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ 
    name: '', 
    description: '' 
  });
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchFeeGroups();
  }, []);

  const fetchFeeGroups = async () => {
    try {
      const response = await feeAPI.getFeeGroups();
      if (response.data.success) {
        setFeeGroups(response.data.data);
      }
    } catch (error) {
      toast.error('Error fetching fee groups');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      let response;
      if (editingItem) {
        response = await feeAPI.updateFeeGroup(editingItem.id, formData);
      } else {
        response = await feeAPI.createFeeGroup(formData);
      }

      if (response.data.success) {
        toast.success(response.data.message);
        setShowModal(false);
        setEditingItem(null);
        setFormData({ name: '', description: '' });
        fetchFeeGroups();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({ 
      name: item.name, 
      description: item.description || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Are you sure you want to delete "${item.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await feeAPI.disableFeeGroup(item.id);
      if (response.data.success) {
        toast.success(response.data.message);
        fetchFeeGroups();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error deleting fee group');
    }
  };

  const handleAddNew = () => {
    setEditingItem(null);
    setFormData({ name: '', description: '' });
    setShowModal(true);
  };

  const filteredFeeGroups = useMemo(() => {
    if (!searchQuery.trim()) return feeGroups;
    
    const query = searchQuery.toLowerCase();
    return feeGroups.filter(item => {
      const name = item.name?.toLowerCase() || '';
      const description = item.description?.toLowerCase() || '';
      
      return name.includes(query) || description.includes(query);
    });
  }, [feeGroups, searchQuery]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p className="mt-4 text-gray-600">Loading fee groups...</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <div className="flex justify-between items-start mb-6 md:mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Fee Group</h1>
          <p className="text-sm md:text-base text-gray-600 mt-1">Manage fee groups for student categories</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-4 md:p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-gray-900">Fee Groups</h3>
          <button 
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors" 
            onClick={handleAddNew}
          >
            <FiPlus /> Add Fee Group
          </button>
        </div>

        <div className="mb-6">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FiSearch className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search by name or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-primary-600 focus:border-transparent text-sm md:text-base"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <FiX className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>
          {searchQuery && (
            <p className="mt-2 text-sm text-gray-600">
              Found {filteredFeeGroups.length} {filteredFeeGroups.length === 1 ? 'fee group' : 'fee groups'}
            </p>
          )}
        </div>

        {feeGroups.length === 0 ? (
          <div className="text-center py-16">
            <FiUsers size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No fee groups yet</h3>
            <p className="text-gray-600 mb-6">Create your first fee group to organize students</p>
            <button 
              className="flex items-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors mx-auto" 
              onClick={handleAddNew}
            >
              <FiPlus /> Create Fee Group
            </button>
          </div>
        ) : filteredFeeGroups.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-lg">
            <FiSearch size={48} className="text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No results found</h3>
            <p className="text-sm text-gray-500 mb-6">
              No fee groups match "{searchQuery}". Try a different search term.
            </p>
            <button
              onClick={() => setSearchQuery('')}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
            >
              Clear Search
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">#</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Group Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Description</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredFeeGroups.map((item, index) => (
                  <tr 
                    key={item.id} 
                    className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-4 py-3 text-sm text-gray-600">{index + 1}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{item.description || '-'}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <button className="btn-icon" onClick={() => handleEdit(item)} title="Edit">
                          <FiEdit2 />
                        </button>
                        <button 
                          className="btn-icon danger"
                          onClick={() => handleDelete(item)}
                          title="Delete"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-5" onClick={() => setShowModal(false)}>
            <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="px-8 py-6 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">{editingItem ? 'Edit' : 'Add'} Fee Group</h2>
                <button className="p-1 hover:bg-gray-100 rounded-md transition-colors" onClick={() => setShowModal(false)}>
                  <FiX size={24} className="text-gray-600" />
                </button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="px-8 py-6 overflow-y-auto flex-1">
                  <div className="mb-5">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Group Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none transition-colors"
                      placeholder="Enter group name"
                      required
                      autoFocus
                    />
                  </div>
                  <div className="mb-5">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none transition-colors"
                      placeholder="Enter description (optional)"
                      rows="3"
                    />
                  </div>
                </div>
                <div className="px-8 py-5 border-t border-gray-200 flex justify-end gap-3">
                  <button
                    type="button"
                    className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
                    onClick={() => setShowModal(false)}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
                    disabled={submitting}
                  >
                    {submitting ? 'Saving...' : editingItem ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeeGroup;
